/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */
import React, { Component } from 'react';
import Measure from 'react-measure';
import PropTypes from 'prop-types';
import Tooltip from '@ba-ui-toolkit/tooltip';
import styles from './Multilinetruncatedtext.scss';

import { populateText, checkHeight, updateTextInfo } from './domMeasureHelpers';

const multilinetruncatedtextClassName = styles.multilinetruncatedtextClassName.replace(/"/g, '');

export default class Multilinetruncatedtext extends Component {
	static propTypes = {
		/** Class names to pass in */
		className: PropTypes.string,
		/** Disables the tooltip */
		disableTooltip: PropTypes.bool,
		/** Number of lines to truncate at */
		numLines: PropTypes.number,
		/** Styles to pass in */
		style: PropTypes.object,
		/** Direction of the text */
		dir: PropTypes.oneOf(['', 'auto', 'rtl', 'ltr']),
		/** Delay before the tooltip appears */
		tooltipDelay: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
		/** text displayed in the tooltip */
		tooltipText: PropTypes.string,
		/** Text to truncate */
		value: PropTypes.string.isRequired,
		/** Enforce the display of tooltip even the text is not long enough (disableTooltip must be set to false) */
		enforceTooltip: PropTypes.bool,
	};

	static defaultProps = {
		className: '',
		disableTooltip: false,
		numLines: 1,
		style: {},
		dir: '',
		tooltipText: '',
		value: '',
		enforceTooltip: false,
	};

	constructor(props) {
		super(props);
		this.node = null;
		this.setNodeRef = element => {
			this.node = element;
		};
		this.state = {
			disableTooltip: this.props.disableTooltip,
			truncatedText: this.props.value,
		};
	}

	_updateTextState(node, textInfo, numLines, width) {
		const lineWidth = width || node.offsetWidth;
		const info = populateText(lineWidth, numLines, textInfo);
		let disableFlag = false;
		if (this.props.disableTooltip) {
			disableFlag = true;
		} else if (!this.props.disableTooltip && this.props.enforceTooltip) {
			disableFlag = false;
		} else {
			disableFlag = info.disableTooltip;
		}
		this.setState({
			truncatedText: info.text,
			disableTooltip: disableFlag,
		});
	}

	_resizeHandler(contentRect) {
		this._updateTextState(
			this.node,
			this.textInfo,
			this.props.numLines,
			contentRect.bounds.width
		);
		const isHeightEqual = checkHeight(
			contentRect.bounds.height,
			this.textInfo.style.lineHeight
		);

		if (!isHeightEqual) {
			this.textInfo = updateTextInfo(
				this.node,
				this.textInfo,
				this.props.value,
				this.props.numLines
			);
			this._updateTextState(this.node, this.textInfo, this.props.numLines);
		}
	}

	componentDidMount() {
		this.textInfo = {
			wordLengths: [],
			ellipsisLength: 0,
			style: {
				lineHeight: this.props.style.lineHeight,
				fontInfo: '',
			},
		};

		this.textInfo = updateTextInfo(
			this.node,
			this.textInfo,
			this.props.value,
			this.props.numLines
		);
		this._updateTextState(this.node, this.textInfo, this.props.numLines);
	}

	render() {
		const { className, style, value, dir, tooltipDelay, tooltipText } = this.props;
		const title = tooltipText || value;
		const text = [];
		for (let i = 0; i < this.state.truncatedText.length; i++) {
			text.push(<div key={i}>{this.state.truncatedText[i]}</div>);
		}

		return (
			<Measure bounds onResize={this._resizeHandler.bind(this)}>
				{({ measureRef }) => (
					<div
						className={`${multilinetruncatedtextClassName} ${className}`}
						dir={dir}
						ref={measureRef}
						style={style}
					>
						<div ref={this.setNodeRef}>
							<Tooltip
								disabled={this.state.disableTooltip}
								delay={tooltipDelay}
								orient="top"
								style={{
									width: '100%',
								}}
								title={title}
							>
								{text}
							</Tooltip>
						</div>
					</div>
				)}
			</Measure>
		);
	}
}
